import { ProgramReward, UserId } from '@cibo/core'
import { ProgramIcon, usePrograms } from '@cibo/programs'
import { Swatch, intuitiveFromNow, useFigures } from '@cibo/ui'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { IconButton, Stack, Typography, useTheme } from '@mui/material'
import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDownloadAgreement } from '../queries'
import { AgreementShape, AgreementStatus } from '../types'
import { LastCommentCell } from './LastCommentCell'
import { UserRefCell } from './UserRefCell'

const ProgramList = ({ programIds }: { programIds: string[] }) => {
  const programs = usePrograms()
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      {programIds?.map(programId => {
        const program = programs.data?.find(program => program.programId === programId)?.program
        return program ? (
          <Stack key={programId} direction="row" spacing={1}>
            <ProgramIcon program={program} size={20} />
            <Typography variant="body2">{program.content.fields.title}</Typography>
          </Stack>
        ) : null
      })}
    </Stack>
  )
}

type ColumnNames =
  | 'acres'
  | 'carbonRemoval'
  | 'dateAgreed'
  | 'dateDeclined'
  | 'dateModified'
  | 'lastComment'
  | 'offerUsd'
  | 'programs'
  | 'revisions'
  | 'status'
  | 'userId'
  | 'year'
  | 'download'

export const usePaginatedAgreementsTableColumns = () => {
  const { t } = useTranslation('@cibo/impact/PaginatedAgreementsTable')
  const {
    palette: {
      success,
      error,
      warning,
      text: { disabled },
    },
  } = useTheme()
  const { acres } = useFigures()

  const { enqueueSnackbar } = useSnackbar()
  const onError = () => {
    enqueueSnackbar(t('downloadError'), { variant: 'error' })
  }
  const { openFile } = useDownloadAgreement(onError)

  const statusToColor = useCallback(
    (status: AgreementStatus) => {
      switch (status) {
        case 'agreed':
          return success.main
        case 'pending':
          return warning.main
        case 'declined':
          return error.main
        default:
          return disabled
      }
    },
    [success.main, warning.main, error.main, disabled]
  )

  return useMemo(
    () =>
      ({
        status: {
          field: 'status',
          headerName: '',
          width: 8,
          sortable: false,
          resizable: false,
          renderCell: ({ value }: GridValueFormatterParams<AgreementStatus>) => (
            <Swatch color={statusToColor(value)} />
          ),
        },
        year: {
          field: 'year',
          headerName: t('year'),
          flex: 0,
          sortable: false,
          valueGetter: ({ row: { createdDate } }: GridValueGetterParams<AgreementShape>) =>
            new Date(createdDate).getFullYear(),
        },
        userId: {
          field: 'userId',
          headerName: t('user'),
          flex: 2,
          sortable: false,
          renderCell: ({ value }: GridValueFormatterParams<UserId>) => (
            <UserRefCell userId={value} />
          ),
        },
        revisions: {
          field: 'revisions',
          headerName: t('revisions'),
          flex: 0,
          sortable: false,
          valueGetter: ({ row }) => row.docRevision,
        },
        lastComment: {
          field: 'lastComment',
          headerName: t('lastComment'),
          flex: 3,
          sortable: false,
          renderCell: ({ row: { agreementId } }: GridRenderCellParams<AgreementShape>) => (
            <LastCommentCell agreementId={agreementId} />
          ),
        },

        dateDeclined: {
          field: 'dateDeclined',
          headerName: t('dateDeclined'),
          flex: 1,
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<AgreementShape>) => row.modifiedDate,
          valueFormatter: ({ value }: GridValueFormatterParams<string>) => intuitiveFromNow(value),
        },
        dateAgreed: {
          field: 'dateAgreed',
          headerName: t('dateAgreed'),
          flex: 1,
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<AgreementShape>) => row.modifiedDate,
          valueFormatter: ({ value }: GridValueFormatterParams<string>) => intuitiveFromNow(value),
        },
        dateModified: {
          field: 'modifiedDate',
          headerName: t('dateModified'),
          flex: 1,
          sortable: false,
          valueFormatter: ({ value }: GridValueFormatterParams<string>) => intuitiveFromNow(value),
        },
        programs: {
          field: 'programs',
          headerName: t('programs'),
          flex: 2,
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<string[], AgreementShape>) =>
            row.fieldStats?.programIds,
          renderCell: ({ value }: GridRenderCellParams<string[]>) =>
            !!value ? <ProgramList programIds={value} /> : null,
        },
        acres: {
          field: 'acres',
          flex: 1,
          headerName: t('acres'),
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<number, AgreementShape>) =>
            row.fieldStats?.acres,
          valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
            !!value ? acres(value) : '',
        },
        carbonRemoval: {
          field: 'carbonRemoval',
          flex: 1,
          headerName: t('carbonRemoval'),
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<number, AgreementShape>) =>
            row.fieldStats?.co2SoilsLbsPerAcre,
          valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
            value !== undefined ? t('carbonRemoved', { count: value }) : '--',
        },
        offerUsd: {
          field: 'offerUsd',
          flex: 1,
          headerName: t('payment'),
          sortable: false,
          valueGetter: ({ row }: GridValueGetterParams<ProgramReward, AgreementShape>) =>
            row.fieldStats?.rewards.offered?.usd,
          valueFormatter: ({ value }: GridValueFormatterParams<ProgramReward>) =>
            t('paymentAmount', {
              count: value?.amount,
              context: value?.token,
            }),
        },
        download: {
          field: 'download',
          headerName: '',
          sortable: false,
          renderCell: ({ row }) =>
            !!row.docPath ? (
              <IconButton
                edge="end"
                aria-label={t('download')}
                onClick={openFile(row)}
                data-testid="download-agreement-button"
              >
                <DownloadRoundedIcon />
              </IconButton>
            ) : null,
        },
      } as Record<ColumnNames, GridColDef>),
    []
  )
}
